/**
 * Push registration for the iOS and Android apps.
 *
 * The web path in push.js does not work here and cannot be made to. A
 * Capacitor app is a WKWebView or an Android WebView, and neither gives the
 * page a usable PushManager: on iOS the Push API is simply absent outside
 * Safari, and on Android the service worker cannot be reached by the OS when
 * the app is closed, which is the only moment a notification matters. So the
 * app registers with the operating system instead and gets back one opaque
 * token, which the sender treats as that device's endpoint.
 *
 * WHY THE FIREBASE PLUGIN RATHER THAN @capacitor/push-notifications. The
 * official plugin is the obvious choice and was the first one tried. On
 * Android it is right - it is Firebase underneath, and the token it returns is
 * an FCM registration token. On iOS it returns the RAW APNs DEVICE TOKEN, and
 * FCM's v1 API will not accept one of those in its `token` field: it wants a
 * token minted by Firebase, which only exists if the Firebase iOS SDK is in
 * the app swapping the APNs token for its own. Sending the APNs token instead
 * fails as an unregistered device, which looks exactly like a permissions
 * problem and is not. This plugin carries that SDK, so both platforms hand
 * back the same kind of token and the sender stays single-path.
 *
 * IMPORTED LAZILY, everywhere. On the web the plugin is dead weight - its web
 * implementation wants the Firebase JS SDK, which this app has no use for
 * because browsers are served by VAPID. Behind a dynamic import it becomes a
 * chunk that a browser never fetches. Same reasoning as the status bar import
 * in AppState.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { supabase, unwrap } from './supabase.js';

/** True in the app, false in every browser including a phone browser. */
export const isNativeApp = () => Capacitor.isNativePlatform();

/*
 * REGISTERED DIRECTLY, NOT IMPORTED.
 *
 * This used to be `await import('@capacitor-firebase/messaging')`, and on the
 * phone that import never settled: no rejection to catch, no bridge call ever
 * made, and every call into push waiting on a module that never arrived. The
 * switch in Settings read "Checking..." for the life of the session.
 *
 * Nothing about that import was needed. The package's entry point is one
 * registerPlugin('FirebaseMessaging') call plus a lazy loader for a web
 * implementation this app never runs - browsers are served by VAPID in
 * push.js. Calling registerPlugin ourselves returns the same proxy, dispatches
 * straight to the Swift and Kotlin plugins, and loads no chunk at all.
 *
 * It also costs a browser nothing, which was the point of the lazy import:
 * registerPlugin comes from @capacitor/core, which is already in the bundle,
 * and the proxy is inert unless something calls it.
 */
const FirebaseMessaging = registerPlugin('FirebaseMessaging');

/* Registered the same way and for the same reason - see above. The badge is
   set by the push payload while the app is closed, which is the only moment
   the operating system will do it; this is the other half, for while the app
   is open and the number is going down as things are read. */
const Badge = registerPlugin('Badge');

/**
 * Puts `count` on the app icon. Silent everywhere it cannot apply.
 *
 * A browser has no icon to badge, and on a phone the permission is part of the
 * notification permission the user may not have granted - neither is a failure
 * worth a toast, or worth letting escape into a caller that is only refreshing
 * a number.
 */
export async function setAppBadge(count) {
  if (!isNativeApp()) return;
  try {
    if (!count || count < 0) await Badge.clear();
    else await Badge.set({ count });
  } catch {
    // Not supported, or not permitted. The number on the icon is not worth
    // interrupting anything for.
  }
}

/** "iOS app" / "Android app", which is what Settings shows for this device. */
const deviceLabel = () => `${Capacitor.getPlatform() === 'ios' ? 'iOS' : 'Android'} app`;

/**
 * Writes one token to push_subscriptions.
 *
 * Upserting on the endpoint is what keeps a reinstall from accumulating rows,
 * exactly as it does for a browser. The keys are null: there is no payload
 * encryption on this path, because FCM holds the APNs key and re-signs for
 * Apple on the way through. 0035 relaxed the NOT NULLs for precisely this.
 */
async function storeToken(userId, token) {
  return unwrap(await supabase
    .from('push_subscriptions')
    .upsert({
      user_id: userId,
      endpoint: token,
      platform: 'fcm',
      p256dh: null,
      auth: null,
      user_agent: deviceLabel(),
    }, { onConflict: 'endpoint' })
    .select()
    .single());
}

/**
 * Asks for permission, gets a token, records it.
 *
 * Returns the stored row, or throws something worth showing the user. Safe to
 * call on the web, where it returns null rather than throwing - the caller
 * should not have to know which platform it is on.
 */
export async function subscribeNativePush(userId) {
  if (!isNativeApp()) return null;

  /* checkPermissions first, because requestPermissions on a previously denied
     install resolves 'denied' immediately without showing anything. Asking
     separately is what lets the message below say "in Settings" rather than
     leaving the user tapping a switch that silently does nothing. */
  let { receive } = await FirebaseMessaging.checkPermissions();
  if (receive === 'prompt' || receive === 'prompt-with-rationale') {
    ({ receive } = await FirebaseMessaging.requestPermissions());
  }
  if (receive !== 'granted') {
    throw new Error('Notifications are turned off for Pulse. Enable them in your device Settings.');
  }

  /* On iOS this resolves only once APNs has answered, which needs a real
     device and a network - the simulator has no APNs connection and will
     reject it. That is expected, and is why the app is tested for this on
     hardware. */
  const { token } = await FirebaseMessaging.getToken();
  if (!token) throw new Error('The device did not return a push token.');

  return storeToken(userId, token);
}

/**
 * Keeps the stored token current for the life of the app.
 *
 * FCM ROTATES TOKENS - on restore from backup, on some updates, and whenever
 * it feels the old one is compromised - and the rotation arrives as an event,
 * not as a failure. Without this listener the row goes stale silently and the
 * phone stops receiving anything, with nothing anywhere saying why. The old
 * row is left to the sender, which deletes it the next time FCM reports it
 * unregistered.
 *
 * Returns a function that removes the listener.
 */
export async function watchNativeToken(userId) {
  if (!isNativeApp()) return () => {};
  const handle = await FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
    if (token) storeToken(userId, token).catch(() => { /* next launch will retry */ });
  });
  return () => handle.remove();
}

/**
 * Routes a tap on a notification to the thing it was announcing.
 *
 * The url is composed by the sender (targetPath in push/index.js) and is the
 * same string the service worker navigates to, so a tap on a phone and a click
 * on a desktop land in the same place.
 *
 * Returns a function that removes the listener.
 */
export async function onNativePushTap(handler) {
  if (!isNativeApp()) return () => {};
  const handle = await FirebaseMessaging.addListener(
    'notificationActionPerformed',
    (event) => {
      // iOS and Android disagree about the shape: one nests the payload under
      // `data`, the other flattens it onto the notification. Reading both is
      // cheaper than branching on the platform.
      const data = event?.notification?.data || {};
      const url = data.url || data.aps?.url;
      if (url) handler(url);
    },
  );
  return () => handle.remove();
}

/**
 * Stops this device receiving, at the device and on the server.
 *
 * deleteToken is what actually silences it: dropping the row alone leaves the
 * OS registration in place, so a stray sender - or this one, before the delete
 * commits - can still reach it.
 */
export async function unsubscribeNativePush() {
  if (!isNativeApp()) return false;
  const { token } = await FirebaseMessaging.getToken().catch(() => ({ token: null }));
  if (token) {
    // Server first, for the same reason as the web path: if the device drops
    // the token and the delete then fails, the row is an address nothing can
    // be delivered to.
    await supabase.from('push_subscriptions').delete().eq('endpoint', token);
  }
  await FirebaseMessaging.deleteToken();
  return true;
}

/** Is this app install registered right now? */
/*
 * Each step, bounded and named.
 *
 * These calls do not fail when they cannot succeed - they wait. getToken() in
 * particular sits forever when the APNs token never arrives or Firebase cannot
 * trade it, and an unbounded await of it is indistinguishable from a slow
 * network, a missing plugin and a wrong bundle id. Naming the step that ran
 * out of time is the difference between "push is broken" and a thing to go
 * and fix.
 */
const STEP_TIMEOUT_MS = 8000;

const step = (name, promise) => Promise.race([
  promise,
  new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`push check timed out at: ${name}`)), STEP_TIMEOUT_MS);
  }),
]);

export async function isNativeSubscribed(userId) {
  if (!isNativeApp()) throw new Error('push check: not running as an app (web path)');
  const { receive } = await step('checkPermissions', FirebaseMessaging.checkPermissions());
  if (receive !== 'granted') return false;
  const { token } = await step('getToken', FirebaseMessaging.getToken());
  if (!token) return false;
  const { data } = await supabase
    .from('push_subscriptions')
    .select('id')
    .eq('user_id', userId)
    .eq('endpoint', token)
    .maybeSingle();
  return !!data;
}
